import { defineElement, html, TemplateElement } from '@webtides/element-js';
import '../../../legacy-library/webcomponents/svg-icon/svg-icon';
import style from './product-search-form.css';
import '../../elements/highlight-text/highlight-text';
import ModalElementEvent from '../../elements/_modal/modal-element/ModalElementEvent';
import SearchHistoryCookie from '../../../js/cookies/search-history';

class ProductSearchForm extends TemplateElement {
    constructor() {
        super({ shadowRender: false, styles: [style] });
        //
        this._suggestions = [];
    }

    properties() {
        return {
            placeholder: 'Produktsuche',
            searchUrl: '/search',
            searchHistoryLabel: 'Zuletzt gesucht',
            searchHistory: [],
            suggestionUrl: '',
            absoluteFlyout: false,
            minLength: 1,
            inputName: 'search',
            method: 'GET',
            value: '',
            isSuggestion: false,
            isHistory: false,
            hasFocus: false,
        };
    }

    watch() {
        return {
            hasFocus: () => {
                this.searchHistory = SearchHistoryCookie.get();
            },
        };
    }

    connected() {
        this.value = this.dataset.searchword ?? '';

        //read history
        if (this.searchHistory.length === 0) {
            this.searchHistory = SearchHistoryCookie.get();
        }

        const parentModal = this.closest('modal-element');
        if (parentModal) {
            parentModal.addEventListener(ModalElementEvent.OPEN, e => {
                this.hasFocus = true;
                window.setTimeout(() => this.$refs.search.focus(), 100);
            });
        }
    }

    afterUpdate() {
        this.$tabstops = Array.from(this.querySelectorAll('[tabindex]'));
    }

    fetch(url, options) {
        if (this._abortController && this._abortController.signal.aborted === false) {
            this._abortController.abort();
        }
        this._abortController = new AbortController();
        const signal = this._abortController.signal;

        const fetchOptions = Object.assign(
            {
                method: 'GET',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                signal,
            },
            options
        );
        return fetch(url, fetchOptions)
            .then(response => {
                this._fetching = false;
                if (!response.ok) {
                    throw Error(response.statusText);
                }
                return response.json();
            })
            .then(response => {
                //set undefined to trigger redraws
                return response;
            });
    }

    events() {
        return {
            '[ref=cancel]': {
                click: () => {
                    this.$refs.search.value = '';
                    this.value = '';
                    this.$refs.search.focus();
                },
            },
            '[ref=search]': {
                input: async ev => {
                    if (this.suggestionUrl === '') {
                        return;
                    }
                    //reset selectionflags
                    this.isSuggestion = false;
                    this.isHistory = false;

                    const { value } = this.$refs.search;

                    try {
                        if (value.length < this.minLength) {
                            throw 'short search';
                        }
                        const response = await this.fetch(`${this.suggestionUrl}?keyword=${value}`);
                        this._suggestions = [{ suggestions: response?.keywords ?? [] }];
                    } catch (e) {
                        this._suggestions = [];
                    }
                    //trigger update by setting new value
                    this.value = value;
                },
            },
            '[data-index]': {
                click: e => {
                    const currentIndex = this.$tabstops.indexOf(e.currentTarget);
                    this.submitElementByTabIndex(currentIndex);
                },
            },
            this: {
                focusin: () => {
                    this.hasFocus = true;
                },
                focusout: e => {
                    window.setTimeout(() => {
                        //timeout is needed .. otherwise body will have focus for a split second and the flyout will close
                        this.hasFocus = this.contains(document.activeElement);
                    }, 100);
                },
                keydown: e => {
                    const currentIndex = this.$tabstops.indexOf(document.activeElement);
                    switch (e.key) {
                        case 'Down':
                        case 'ArrowDown':
                            if (currentIndex + 1 < this.$tabstops.length) {
                                e.preventDefault();
                                this.$tabstops[currentIndex + 1].focus();
                            }
                            break;
                        case 'Up':
                        case 'ArrowUp':
                            if (currentIndex > 0) {
                                e.preventDefault();
                                this.$tabstops[currentIndex - 1].focus();
                            }
                            break;
                        case 'Enter':
                            this.submitElementByTabIndex(currentIndex);
                            break;
                    }
                },
                submit: () => {
                    if (this.hasValue && this.hasSuggestions) {
                        this.submitElementByTabIndex(1);
                    }
                },
            },
        };
    }

    submitElementByTabIndex(tabIndex) {
        const el = this.$tabstops[tabIndex];
        const index = el?.dataset.index ?? false;
        if (!index) return;

        let value;
        if (index && el.dataset.entityIndex) {
            //suggestion
            this.isHistory = false;
            this.isSuggestion = true;
            value = this._suggestions[el.dataset.entityIndex].suggestions[index];
        } else if (index) {
            //history hit
            this.isHistory = true;
            this.isSuggestion = false;
            value = this.searchHistory[index];
        }

        if (value) {
            //needs to be triggered by dom reference or lit wont replace it if there is a user input
            this.$refs.search.value = value;
            this.value = value;
            // rerender and submit
            this.requestUpdate().then(() => this.$refs.form.submit());
        }
    }

    get hasValue() {
        return this.value !== '';
    }

    get hasSuggestions() {
        return this._suggestions.length > 0 && this._suggestions.some(group => group.suggestions.length > 0);
    }

    get hasHistory() {
        return this.searchHistory.length > 0;
    }

    get hasFlyout() {
        return this.hasSuggestions || this.hasHistory;
    }

    renderEntitySuggestions() {
        let tabIndex = 2;
        return this._suggestions.map(
            (entity, entityIndex) => html`
                ${entity.label ? html` <div class="font-medium px-4 py-2 flex items-center">${entity.label}</div>` : ``}
                ${entity.suggestions.map(
                    (suggestion, suggestionIndex) => html`
                        <div
                            tabindex=${tabIndex++}
                            class="p-2 text text-grey-400 hover:bg-grey-100 focus:bg-grey-50 hover:text-brand-500 outline-none cursor-pointer"
                            data-index="${suggestionIndex}"
                            data-entity-index="${entityIndex}"
                        >
                            <svg-icon class="h-3 w-3 mx-2" name="search"></svg-icon>
                            <highlight-text
                                .search=${this.value}
                                .text=${suggestion}
                                highlight-class="underline font-medium"
                            ></highlight-text>
                        </div>
                    `
                )}
            `
        );
    }

    renderHistory() {
        return html`
            <div class="font-medium px-4 py-2 flex items-center">${this.searchHistoryLabel}</div>
            ${this.searchHistory.map(
                (suggestion, suggestionIndex) =>
                    html`
                        <div
                            class="p-2 text text-grey-400 hover:bg-grey-50 focus:bg-grey-100 hover:text-brand-500 outline-none cursor-pointer"
                            tabindex=${suggestionIndex + 2}
                            data-index="${suggestionIndex}"
                        >
                            <!-- todo add history icon ??-->
                            <svg-icon class="h-3 w-3 mx-2" name="clock"></svg-icon>
                            ${suggestion}
                        </div>
                    `
            )}
        `;
    }

    renderSuggestions() {
        if (!this.hasFocus || !this.hasFlyout) return;

        return html` <div
            class="search-suggestions leading-relaxed border border-t-0 border-grey-300 ${this.absoluteFlyout
                ? 'absolute left-0 right-0 z-10 shadow'
                : ''}"
        >
            ${this.hasSuggestions ? this.renderEntitySuggestions() : this.renderHistory()}
        </div>`;
    }

    template() {
        return html`
            <form
                ref="form"
                action="${this.searchUrl}"
                method="${this.method}"
                autocomplete="off"
                class="w-full text-grey-800 relative"
            >
                <div
                    class="input-search relative py-2 px-4 flex items-center border border-grey-300
                        ${this.hasFocus && this.hasFlyout ? 'border-b-0' : ''}"
                >
                    <input
                        ref="search"
                        type="text"
                        tabindex="1"
                        name="${this.inputName}"
                        value="${this.value}"
                        required
                        placeholder="${this.placeholder}"
                        autocomplete="off"
                        class="outline-none text-2xl rfs-text no-rfs-margin leading-snug w-full border-0 flex-auto min-w-0 truncate"
                    />

                    ${this.hasFocus && this.hasValue
                        ? html`
                              <button
                                  ref="cancel"
                                  type="reset"
                                  class="relative close-button flex-shrink-0 mr-4 md:mr-6 h-4 w-4 md:h-6 md:w-6 outline-none bg-grey-200 rounded-full text-white"
                              >
                                  <svg-icon class="absolute inset-0 color-white" name="close"></svg-icon>
                              </button>
                          `
                        : ''}
                    <button
                        type="submit"
                        class="search-button flex-shrink-0 relative h-6 w-6 md:h-8 md:w-8 outline-none focus:color-brand-400"
                    >
                        <svg-icon class="h-full w-full" name="search"></svg-icon>
                    </button>
                </div>

                ${this.renderSuggestions()}

                <input type="hidden" name="suggestion" value="${this.isSuggestion}" />
                <input type="hidden" name="history" value="${this.isHistory}" />
            </form>
        `;
    }
}

defineElement('product-search-form', ProductSearchForm);
